import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DevLogin from '../components/DevLogin';
import StateSelect from '../components/StateSelect';
import './AuthPages.css';

function LoginPage() {
  const [activeTab, setActiveTab] = useState('login');
  const [loginFormData, setLoginFormData] = useState({
    email: '',
    password: '',
  });
  const [registerFormData, setRegisterFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, register } = useAuth();
  const navigate = useNavigate();

  // Check if dev login is enabled via environment variable
  const isDevLoginEnabled = process.env.REACT_APP_ENABLE_DEV_LOGIN === 'true';

  const handleLoginChange = (e) => {
    setLoginFormData({
      ...loginFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleRegisterChange = (e) => {
    setRegisterFormData({
      ...registerFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await login(loginFormData);
      const user = response.user;

      // Redirect based on role
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'driver') {
        navigate('/driver');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await register(registerFormData);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // If dev login is enabled, show the DevLogin component
  if (isDevLoginEnabled) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-tabs">
            <button
              className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('login');
                setError('');
              }}
            >
              Login
            </button>
            <button
              className={`auth-tab ${activeTab === 'register' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('register');
                setError('');
              }}
            >
              Register
            </button>
          </div>

          {activeTab === 'login' ? (
            <DevLogin />
          ) : (
            <form onSubmit={handleRegisterSubmit}>
              <Link to="/register-driver" className="driver-redirect-btn">
                I am a driver →
              </Link>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="first_name">First Name</label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={registerFormData.first_name}
                    onChange={handleRegisterChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="last_name">Last Name</label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={registerFormData.last_name}
                    onChange={handleRegisterChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={registerFormData.phone}
                  onChange={handleRegisterChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="register_email">Email</label>
                <input
                  type="email"
                  id="register_email"
                  name="email"
                  value={registerFormData.email}
                  onChange={handleRegisterChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="register_password">Password</label>
                <input
                  type="password"
                  id="register_password"
                  name="password"
                  value={registerFormData.password}
                  onChange={handleRegisterChange}
                  required
                />
              </div>

              <h3>Delivery Address</h3>

              <div className="form-group">
                <label htmlFor="address">Street Address</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={registerFormData.address}
                  onChange={handleRegisterChange}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={registerFormData.city}
                    onChange={handleRegisterChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="state">State</label>
                  <StateSelect
                    id="state"
                    name="state"
                    value={registerFormData.state}
                    onChange={handleRegisterChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="zip_code">ZIP Code</label>
                  <input
                    type="text"
                    id="zip_code"
                    name="zip_code"
                    value={registerFormData.zip_code}
                    onChange={handleRegisterChange}
                  />
                </div>
              </div>

              {error && <div className="error">{error}</div>}

              <button
                type="submit"
                className="btn"
                disabled={loading}
                style={{
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  backgroundImage: 'none',
                  opacity: 1
                }}
              >
                {loading ? 'Registering...' : 'Register'}
              </button>
            </form>
          )}

          <p className="auth-link">
            Want to become a driver? <Link to="/register-driver">Apply here</Link>
          </p>
        </div>
      </div>
    );
  }

  // Normal production login/register form with tabs
  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-tabs">
          <button
            className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('login');
              setError('');
            }}
          >
            Login
          </button>
          <button
            className={`auth-tab ${activeTab === 'register' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('register');
              setError('');
            }}
          >
            Register
          </button>
        </div>

        {activeTab === 'login' ? (
          <form onSubmit={handleLoginSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={loginFormData.email}
                onChange={handleLoginChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={loginFormData.password}
                onChange={handleLoginChange}
                required
              />
            </div>

            {error && <div className="error">{error}</div>}

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegisterSubmit}>
            <Link to="/register-driver" className="driver-redirect-btn">
              I am a driver →
            </Link>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="first_name">First Name</label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={registerFormData.first_name}
                  onChange={handleRegisterChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="last_name">Last Name</label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={registerFormData.last_name}
                  onChange={handleRegisterChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={registerFormData.phone}
                onChange={handleRegisterChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="register_email">Email</label>
              <input
                type="email"
                id="register_email"
                name="email"
                value={registerFormData.email}
                onChange={handleRegisterChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="register_password">Password</label>
              <input
                type="password"
                id="register_password"
                name="password"
                value={registerFormData.password}
                onChange={handleRegisterChange}
                required
              />
            </div>

            <h3>Delivery Address</h3>

            <div className="form-group">
              <label htmlFor="address">Street Address</label>
              <input
                type="text"
                id="address"
                name="address"
                value={registerFormData.address}
                onChange={handleRegisterChange}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">City</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={registerFormData.city}
                  onChange={handleRegisterChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="state">State</label>
                <StateSelect
                  id="state"
                  name="state"
                  value={registerFormData.state}
                  onChange={handleRegisterChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="zip_code">ZIP Code</label>
                <input
                  type="text"
                  id="zip_code"
                  name="zip_code"
                  value={registerFormData.zip_code}
                  onChange={handleRegisterChange}
                />
              </div>
            </div>

            {error && <div className="error">{error}</div>}

            <button
              type="submit"
              className="btn"
              disabled={loading}
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                backgroundImage: 'none',
                opacity: 1
              }}
            >
              {loading ? 'Registering...' : 'Register'}
            </button>
          </form>
        )}

        <p className="auth-link">
          Want to become a driver? <Link to="/register-driver">Apply here</Link>
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
